import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useChats } from '../contexts/ChatsContext';

function TrashButton({ onClick, title = 'Delete chat' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="w-9 h-9 rounded-lg flex items-center justify-center text-[#8A8B91] hover:bg-wash hover:text-[#D14343] transition-colors flex-shrink-0"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <path d="M3 6h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8 6V4.5A2.5 2.5 0 0 1 10.5 2h3A2.5 2.5 0 0 1 16 4.5V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M6.5 6l1 15A2 2 0 0 0 9.5 23h5a2 2 0 0 0 2-2l1-15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M10 11v7M14 11v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>
  );
}

function Row({ s, active, onClick, onDelete, time }) {
  return (
    <div
      className="w-full flex items-start gap-3 px-5 py-3.5 border-b border-wash hover:bg-[#FAFAFA] transition-colors"
      style={{ background: active ? '#E9F6F6' : undefined }}
    >
      <button type="button" onClick={onClick} className="flex-1 min-w-0 text-left flex gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#F3F4F6' }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 3.5h8M3 6.5h6M3 9.5h8" stroke="#063F5A" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-ink leading-tight truncate">{s.title || 'Chat'}</div>
          <div className="text-xs text-copy mt-0.5 leading-relaxed truncate">
            {s.preview || '—'}
          </div>
          <div className="text-xs text-muted mt-1">{time}</div>
        </div>
      </button>
      <TrashButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete?.();
        }}
      />
    </div>
  );
}

export function HistoryPage() {
  const nav = useNavigate();
  const { setTopbar, setTopbarActions } = useAppShell();
  const { sessions, activeId, fmtTime, setActive, deleteSession } = useChats();
  const [query, setQuery] = useState('');

  useEffect(() => {
    setTopbar('History', 'All chat sessions');
    setTopbarActions(null);
  }, [setTopbar, setTopbarActions]);

  const list = useMemo(() => {
    const sorted = [...sessions].sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
    if (!query.trim()) return sorted;
    const q = query.toLowerCase();
    return sorted.filter(s =>
      (s.title || '').toLowerCase().includes(q) ||
      (s.preview || '').toLowerCase().includes(q)
    );
  }, [sessions, query]);

  return (
    <div className="h-full overflow-hidden flex flex-col">
      <div className="px-5 py-3 border-b border-wash bg-white flex-shrink-0">
        <div className="relative">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8B91] pointer-events-none">
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M9.5 9.5L12 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <input
            type="search"
            placeholder="Search chats…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm bg-wash border border-transparent rounded-lg text-ink placeholder-[#8A8B91] outline-none focus:border-teal-400 focus:bg-white transition-colors"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto bg-white">
        {list.length === 0 ? (
          <div className="px-6 py-8 text-sm text-muted">
            {query.trim() ? 'No chats match your search.' : 'No history yet.'}
          </div>
        ) : (
          list.map(s => (
            <Row
              key={s.id}
              s={s}
              active={s.id === activeId}
              time={fmtTime(s.updatedAt || s.createdAt)}
              onClick={() => {
                setActive(s.id);
                nav('/ace', { state: { chatSessionId: s.id } });
              }}
              onDelete={() => { deleteSession(s.id); }}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default HistoryPage;

